import * as THREE from 'three';
import { MISSION } from '../config/tuning';
import { WARHEADS, type WarheadKindId } from '../config/targets';
import { bus } from '../core/EventBus';
import type { Checksum, Checksummable } from '../core/Checksum';
import { ObjectiveTracker, type ObjectiveWorld } from './Objectives';
import { TriggerSystem, type TriggerWorld } from './Triggers';
import { scoreMission, type MissionScore, type RunSummary } from './Scoring';
import type { Mission } from './schema';

/**
 * Binds a mission (data) to the running simulation (SPEC §3.10).
 *
 * The runner owns the mission's clock, its 10 Hz evaluation, and the accumulators the debrief and
 * score need. It deliberately does *not* own the world: it is handed a snapshot each tick and
 * pushes back a set of desired states (which threats are live, which waypoints are visible). That
 * keeps missions from reaching into systems, which is what makes them safe to author as data.
 *
 * Distances in metres, times in seconds.
 */

export const MissionPhase = {
  Running: 'RUNNING',
  Succeeded: 'SUCCEEDED',
  Failed: 'FAILED',
} as const;
export type MissionPhaseId = (typeof MissionPhase)[keyof typeof MissionPhase];

/** The per-tick snapshot the runner needs. Assembled by main; nothing is retained. */
export interface MissionSnapshot {
  readonly positionM: THREE.Vector3;
  readonly altitudeAglM: number;
  readonly integrity: number;
  readonly fuelFraction: number;
  readonly crashed: boolean;
  readonly threatStates: ReadonlyMap<string, string>;
  readonly photographed: ReadonlySet<string>;
}

export class MissionRunner implements Checksummable {
  readonly mission: Mission;
  readonly objectives: ObjectiveTracker;
  readonly triggers: TriggerSystem;

  phase: MissionPhaseId = MissionPhase.Running;
  /** Set once the outcome is known; the score is computed after the impact-cam delay. */
  score: MissionScore | null = null;
  outcomeReason = '';

  private elapsedS = 0;
  private accumulatorS = 0;
  private resolveAtS = -1;

  private readonly destroyed = new Set<string>();
  private readonly struck = new Set<string>();
  private readonly waypoints = new Map<string, THREE.Vector3>();

  // Score accumulators.
  private altitudeSumM = 0;
  private altitudeSamples = 0;
  private aimPointDistanceM: number | null = null;
  private missileHits = 0;
  private collateralStrikes = 0;
  private readonly evadedMissDistancesM: number[] = [];
  private finalIntegrity = 1;
  private finalFuelFraction = 1;

  private readonly detach: (() => void)[] = [];

  constructor(mission: Mission) {
    this.mission = mission;
    this.objectives = new ObjectiveTracker(mission);
    this.triggers = new TriggerSystem(mission, this.objectives);

    for (let i = 0; i < mission.route.length; i += 1) {
      const waypoint = mission.route[i];
      this.waypoints.set(
        waypoint.id,
        new THREE.Vector3(waypoint.position[0], waypoint.position[1], waypoint.position[2]),
      );
    }

    this.detach.push(
      bus.on('target:impact', (hit) => {
        // The designation is the mission's target id — that is how a data-authored objective
        // refers to something the simulation destroyed.
        if (hit.destroyed) this.destroyed.add(hit.designation);
        this.struck.add(hit.designation);
        this.aimPointDistanceM = hit.aimPointDistanceM;
        if (this.isCollateral(hit.designation)) this.collateralStrikes += 1;
        // The run ends on impact — this is a one-way drone — but not instantly, so the impact
        // cam has room to play before the debrief takes the screen.
        if (this.resolveAtS < 0) this.resolveAtS = this.elapsedS + MISSION.outcomeDelayS;
      }),
    );

    this.detach.push(
      bus.on('missile:detonated', (hit) => {
        if (hit.damage > 0) this.missileHits += 1;
        this.evadedMissDistancesM.push(hit.missDistanceM);
      }),
    );

    this.detach.push(
      bus.on('missile:expired', (miss) => {
        // A missile that ran out of energy still had a closest approach, and how close it got is
        // exactly what the evasion term is measuring.
        this.evadedMissDistancesM.push(miss.missDistanceM);
      }),
    );
  }

  dispose(): void {
    for (let i = 0; i < this.detach.length; i += 1) this.detach[i]();
    this.detach.length = 0;
  }

  get timeS(): number {
    return this.elapsedS;
  }

  /**
   * The checkpoint the player is currently heading for, or `null` when none is outstanding.
   *
   * The first pending `reach` objective in mission order — objectives are authored in the order
   * they are meant to be flown, so "first pending" is "next". Returns the capture radius alongside
   * the position because the world marker draws the actual radius: the ring a player flies through
   * is the sphere the objective tests, not a decoration sized by eye.
   */
  nextCheckpoint(): { id: string; positionM: THREE.Vector3; radiusM: number } | null {
    for (let i = 0; i < this.objectives.statuses.length; i += 1) {
      const status = this.objectives.statuses[i];
      if (status.kind !== 'reach' || status.state !== 'pending') continue;
      const ref = status.targetRef;
      if (ref === undefined) continue;
      const positionM = this.waypoints.get(ref);
      if (positionM === undefined) continue;
      return { id: ref, positionM, radiusM: this.checkpointRadiusM(status.id) };
    }
    return null;
  }

  /** Capture radius of a `reach` objective, metres. Mission override, or the default. */
  private checkpointRadiusM(objectiveId: string): number {
    for (let i = 0; i < this.mission.objectives.length; i += 1) {
      const objective = this.mission.objectives[i];
      if (objective.id === objectiveId) return objective.radiusM ?? MISSION.waypointRadiusM;
    }
    return MISSION.waypointRadiusM;
  }

  /** Waypoints the player can currently see, in route order. */
  visibleRoute(): { id: string; label: string; type: string; positionM: THREE.Vector3 }[] {
    const out: { id: string; label: string; type: string; positionM: THREE.Vector3 }[] = [];
    for (let i = 0; i < this.mission.route.length; i += 1) {
      const waypoint = this.mission.route[i];
      if (!this.triggers.effects.revealedWaypoints.has(waypoint.id)) continue;
      const positionM = this.waypoints.get(waypoint.id);
      if (positionM === undefined) continue;
      out.push({ id: waypoint.id, label: waypoint.label, type: waypoint.type, positionM });
    }
    return out;
  }

  /**
   * Advance the mission. `dtS` is the fixed simulation step; evaluation is decimated to 10 Hz.
   *
   * Runs after the world has been stepped, so the snapshot describes the state the player is
   * actually in rather than the one they were in a tick ago.
   */
  update(dtS: number, snapshot: MissionSnapshot): void {
    if (this.phase !== MissionPhase.Running) return;

    this.elapsedS += dtS;
    this.finalIntegrity = snapshot.integrity;
    this.finalFuelFraction = snapshot.fuelFraction;

    // Ingress altitude is sampled only while the run is still an ingress: averaging in the dive
    // and the climb-out would reward the wrong thing.
    if (this.resolveAtS < 0) {
      this.altitudeSumM += snapshot.altitudeAglM;
      this.altitudeSamples += 1;
    }

    this.accumulatorS += dtS;
    const interval = 1 / MISSION.evaluateHz;
    while (this.accumulatorS >= interval) {
      this.accumulatorS -= interval;
      this.evaluate(snapshot);
    }

    if (snapshot.crashed && this.resolveAtS < 0) {
      this.resolveAtS = this.elapsedS;
      this.outcomeReason = 'The aircraft was lost.';
    }

    if (this.resolveAtS >= 0 && this.elapsedS >= this.resolveAtS) this.resolve();
  }

  private evaluate(snapshot: MissionSnapshot): void {
    const objectiveWorld: ObjectiveWorld = {
      positionM: snapshot.positionM,
      elapsedS: this.elapsedS,
      integrity: snapshot.integrity,
      destroyed: this.destroyed,
      waypoints: this.waypoints,
      struck: this.struck,
      photographed: snapshot.photographed,
    };
    const before = this.objectiveSignature();
    this.objectives.update(objectiveWorld);
    this.announceChanges(before);

    const triggerWorld: TriggerWorld = {
      positionM: snapshot.positionM,
      elapsedS: this.elapsedS,
      altitudeAglM: snapshot.altitudeAglM,
      integrity: snapshot.integrity,
      threatStates: snapshot.threatStates,
    };
    this.triggers.update(triggerWorld);

    // A failed primary ends the run immediately: there is nothing left to fly for, and letting it
    // continue would quietly turn a loss into a longer loss.
    if (this.objectives.anyPrimaryFailed && this.resolveAtS < 0) {
      this.resolveAtS = this.elapsedS;
      this.outcomeReason = 'A primary objective failed.';
    }
  }

  private objectiveSignature(): string[] {
    const out: string[] = [];
    for (let i = 0; i < this.objectives.statuses.length; i += 1) {
      out.push(this.objectives.statuses[i].state);
    }
    return out;
  }

  private announceChanges(before: readonly string[]): void {
    for (let i = 0; i < this.objectives.statuses.length; i += 1) {
      const status = this.objectives.statuses[i];
      if (status.state === before[i]) continue;
      bus.emit('mission:objective', {
        id: status.id,
        state: status.state,
        description: status.description,
      });
    }
  }

  /** Whether a struck id was something the mission told the player to leave alone. */
  private isCollateral(id: string): boolean {
    for (let i = 0; i < this.mission.objectives.length; i += 1) {
      const objective = this.mission.objectives[i];
      if (objective.kind === 'avoid' && objective.targetRef === id) return true;
    }
    return false;
  }

  private resolve(): void {
    this.objectives.finalise();

    const warhead: WarheadKindId = this.mission.loadout.warhead;
    const summary: RunSummary = {
      elapsedS: this.elapsedS,
      integrity: this.finalIntegrity,
      fuelFraction: this.finalFuelFraction,
      aimPointDistanceM: this.aimPointDistanceM,
      aimPointReferenceM: WARHEADS[warhead].effectRadiusM,
      meanIngressAltitudeM:
        this.altitudeSamples === 0 ? 0 : this.altitudeSumM / this.altitudeSamples,
      evadedMissDistancesM: this.evadedMissDistancesM,
      missileHits: this.missileHits,
      collateralStrikes: this.collateralStrikes,
    };

    this.score = scoreMission(this.mission, this.objectives, summary);
    const success = this.objectives.allPrimaryComplete;
    this.phase = success ? MissionPhase.Succeeded : MissionPhase.Failed;
    if (this.outcomeReason === '') {
      this.outcomeReason = success ? 'All primary objectives complete.' : 'Primary objectives incomplete.';
    }

    bus.emit('mission:outcome', {
      missionId: this.mission.id,
      success,
      reason: this.outcomeReason,
    });
  }

  /** The accumulated run summary, for the debrief. Valid once resolved. */
  runSummary(): RunSummary {
    return {
      elapsedS: this.elapsedS,
      integrity: this.finalIntegrity,
      fuelFraction: this.finalFuelFraction,
      aimPointDistanceM: this.aimPointDistanceM,
      aimPointReferenceM: WARHEADS[this.mission.loadout.warhead].effectRadiusM,
      meanIngressAltitudeM:
        this.altitudeSamples === 0 ? 0 : this.altitudeSumM / this.altitudeSamples,
      evadedMissDistancesM: this.evadedMissDistancesM,
      missileHits: this.missileHits,
      collateralStrikes: this.collateralStrikes,
    };
  }

  checksum(into: Checksum): void {
    into.addFloat(this.elapsedS);
    into.addInt(this.phase === MissionPhase.Running ? 0 : this.phase === MissionPhase.Succeeded ? 1 : 2);
    this.objectives.checksum(into);
    this.triggers.checksum(into);
  }
}
